<template>
    <Modal id="membership-cancellation-modal"
           :title="modalTitle"
           :icon="modalIcon"
           size="lg"
           :cancel="cancel"
           :ok="ok">
        <p class="text-center">
            {{ l('MEMBERSHIP_CANCELLATION.INTRO__' + uppercaseMode) }}
            <br />
            {{ l('MEMBERSHIP_CANCELLATION.SELECT_AMOUNT__' + uppercaseMode) }}
        </p>

        <MembershipCancellationMembership v-for="membership in memberships"
                                          :key="membership.ID"
                                          :membership="membership"
                                          :selected-refund-type="getSelectedRefundType(membership.ID)"
                                          @select-refund-type="selectRefundType" />

        <template v-slot:footer>
            <div class="modl-footer">
                <button class="btn btn-lg btn-default"
                        @click.prevent="cancel">
                    {{ l('MEMBERSHIP_CANCELLATION.CANCEL_BTN_LABEL__' + uppercaseMode) }}
                </button>

                <div class="membership-cancellation-total">
                    <span>
                        {{ l('MEMBERSHIP_CANCELLATION.TOTAL_REFUND__' + uppercaseMode) }}:
                        <span class="membership-cancellation-total__amount">{{ currencyFilter(totalRefund) }}</span>
                    </span>
                </div>

                <button class="btn btn-lg"
                        :class="okBtnClass"
                        @click.prevent="ok">
                    {{ l('MEMBERSHIP_CANCELLATION.OK_BTN_LABEL__' + uppercaseMode) }}
                </button>
            </div>
        </template>
    </Modal>
</template>

<script>
import BigNumber from 'bignumber.js';
import MembershipCancellationMembership
    from '@/epos/vue/modals/MembershipCancellation/MembershipCancellationMembership';
import MembershipRefundType from '@/lib/Memberships/MembershipRefundType';
import Modal from '@/vue/components/Modals/Modal';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    components: { Modal, MembershipCancellationMembership },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {Membership[]}}
         */
        memberships: {
            type: Array,
            required: true,
        },

        /**
         * Mode can be either 'cancel' or 'change'.
         * It affects the wording shown in the modal.
         * If in change plan mode it says that the user is selecting how much credit to give
         * towards the new plan when cancelling off the previous memberships.
         */
        mode: {
            type: String,
            default: 'cancel',
        },
    },

    data() {
        return {
            loading: true,

            refundTypes: Object.values(MembershipRefundType),

            selectedRefundTypes: {},

            totalRefund: 0.00,
        };
    },

    computed: {
        uppercaseMode() {
            return this.mode.toUpperCase();
        },

        modalTitle() {
            return 'MEMBERSHIP_CANCELLATION.MODAL_TITLE__' + this.uppercaseMode;
        },

        modalIcon() {
            if (this.mode === 'change') {
                return 'fa-right-left';
            }

            return 'fa-users-slash';
        },

        okBtnClass() {
            if (this.mode === 'change') {
                return 'btn-primary';
            }

            return 'btn-danger';
        },
    },

    created() {
        // Fetch the refund amounts from the API.
        container.membershipService.calculateMembershipRefunds(
            this.memberships,
            () => {
                this.loading = false;
                this.calculate();
            },
        );

        // Select the 'none' refund type by default.
        this.memberships.forEach(
            /**
             * @param {Membership} membership
             */
            (membership) => {
                this.selectRefundType(membership.ID, MembershipRefundType.NONE);
            },
        );
    },

    methods: {
        calculate() {
            let total = new BigNumber(0.00);
            this.memberships.forEach(
                /**
                 * @param {Membership} membership
                 */
                (membership) => {
                    if (membership.refundAmount) {
                        let selectedType = this.getSelectedRefundType(membership.ID);
                        if (selectedType) {
                            total = total.plus(
                                membership.refundAmount[selectedType + 'Total'],
                            );
                        }
                    }
                },
            );

            this.totalRefund = total.toNumber();
        },

        /**
         * @param {number} membershipID
         * @return {number|null}
         */
        getSelectedRefundType(membershipID) {
            return this.selectedRefundTypes.hasOwnProperty(membershipID)
                ? this.selectedRefundTypes[membershipID]
                : null;
        },

        /**
         * @param {number} membershipID
         * @param {string} type
         */
        selectRefundType(membershipID, type) {
            this.selectedRefundTypes[membershipID] = type;
            this.calculate();
        },

        ok() {
            const refundCartItems = container.membershipService.generateMembershipRefundCartItems(
                this.memberships,
                this.selectedRefundTypes,
                null,
            );

            this.resolve({
                memberships: this.memberships,
                refundCartItems,
            });
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../less/variables";

#membership-cancellation-modal {
    .modl-body {
        background: @form-group-container-bg;
    }

    p {
        margin: 5px 0 15px;
    }

    // All currency labels
    strong {
        display: block;
        font-size: 18px;
    }

    .membership-cancellation-total {
        flex-grow: 1;
        flex-shrink: 1;

        display: flex;
        align-items: center;
        justify-content: center;

        font-size: 20px;
        font-weight: 700;

        &__amount {
            color: @dt-pink;
        }
    }
}

@media (max-width: @mobile-max) {
    #membership-cancellation-modal {
        .modl-footer {
            display: block;

            .btn {
                display: block;
                width: 100%;
            }

            .membership-cancellation-total {
                margin: 10px 0;
            }
        }
    }
}
</style>
